import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { useI18n } from "@/lib/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Camera, MapPin, RefreshCw, LogIn, LogOut } from "lucide-react";

function useCamera() {
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);
  const start = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      });
      setStream(s);
      if (videoRef.current) videoRef.current.srcObject = s;
    } catch (e) {
      setError(e.message);
    }
  };
  const stop = () => {
    if (stream) stream.getTracks().forEach((t) => t.stop());
    setStream(null);
  };
  const snap = () => {
    if (!videoRef.current) return null;
    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth || 480;
    canvas.height = videoRef.current.videoHeight || 360;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(videoRef.current, 0, 0);
    return canvas.toDataURL("image/jpeg", 0.7);
  };
  useEffect(() => () => stop(), []); // eslint-disable-line
  return { videoRef, stream, start, stop, snap, error };
}

function getGeo() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("No geolocation"));
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ latitude: p.coords.latitude, longitude: p.coords.longitude }),
      (e) => reject(e),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  });
}

export default function StaffCheckInCard({ compact = false }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const cam = useCamera();
  const [photo, setPhoto] = useState(null);
  const [geo, setGeo] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getGeo().then(setGeo).catch(() => setGeo(null));
  }, []);

  const captureFlow = async () => {
    if (!cam.stream) await cam.start();
    setTimeout(() => {
      const dataUrl = cam.snap();
      if (dataUrl) setPhoto(dataUrl);
      cam.stop();
    }, 400);
  };

  const submit = async (action) => {
    if (!geo) {
      toast.error(t("geoRequired"));
      try { const g = await getGeo(); setGeo(g); } catch { return; }
    }
    if (!photo) {
      toast.error("Please capture your face first.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/attendance/staff", {
        method: "geo_face",
        action,
        latitude: geo.latitude,
        longitude: geo.longitude,
        photo_data_url: photo,
      });
      toast.success(`${action === "check_in" ? t("checkIn") : t("checkOut")} recorded`);
      setPhoto(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="pmm-pop rounded-[1.5rem] border-border/60 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" data-testid="staff-checkin-card">
      <CardContent className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="font-display text-sm font-extrabold uppercase tracking-widest text-blue-950">
              {t("hello")}, {user?.name}
            </h3>
            <p className="text-sm font-semibold text-muted-foreground">
              {t("face")} + {t("location")}
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
            <MapPin className="h-4 w-4 text-orange-500" />
            {geo ? (
              <span className="num">
                {geo.latitude.toFixed(4)}, {geo.longitude.toFixed(4)}
              </span>
            ) : (
              <span>Fetching location…</span>
            )}
          </div>
        </div>

        <div className="relative overflow-hidden rounded-xl bg-black/90 aspect-video">
          {photo ? (
            <img src={photo} alt="captured" className="h-full w-full object-cover" />
          ) : (
            <video
              ref={cam.videoRef}
              autoPlay
              playsInline
              muted
              className="h-full w-full object-cover"
            />
          )}
          {!cam.stream && !photo && (
            <div className="absolute inset-0 grid place-items-center text-white/70 text-sm">
              Tap {t("captureFace")}
            </div>
          )}
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          {photo ? (
            <Button
              variant="outline"
              onClick={() => { setPhoto(null); cam.start(); }}
              className="rounded-full h-11 gap-2"
              data-testid="recapture-btn"
            >
              <RefreshCw className="h-4 w-4" />
              {t("recapture")}
            </Button>
          ) : (
            <Button
              onClick={captureFlow}
              className="col-span-2 h-11 gap-2 rounded-full bg-gradient-to-r from-orange-600 to-orange-500 font-display text-sm font-extrabold uppercase tracking-wide text-white shadow-lg shadow-orange-500/40 transition-transform duration-300 hover:scale-105"
              data-testid="capture-face-btn"
            >
              <Camera className="h-4 w-4" />
              {t("captureFace")}
            </Button>
          )}
          {photo && (
            <>
              <Button
                onClick={() => submit("check_in")}
                disabled={busy}
                className="h-11 gap-2 rounded-full bg-gradient-to-r from-orange-600 to-orange-500 font-bold text-white shadow-md shadow-orange-500/30 transition-transform duration-300 hover:scale-105"
                data-testid="check-in-btn"
              >
                <LogIn className="h-4 w-4" />
                {t("checkIn")}
              </Button>
              <Button
                onClick={() => submit("check_out")}
                disabled={busy}
                className="h-11 gap-2 rounded-full bg-gradient-to-r from-blue-900 to-blue-700 font-bold text-white shadow-md shadow-blue-800/30 transition-transform duration-300 hover:scale-105"
                data-testid="check-out-btn"
              >
                <LogOut className="h-4 w-4" />
                {t("checkOut")}
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
